import { FormEvent, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { api, del, errorMessage, post } from "@/lib/api";

const NOTE_CAP = 3;

type SchoolNote = {
  id: string;
  body: string;
  authorName: string | null;
  at: string;
};

function postedLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function SchoolAnnouncements({
  schoolId,
  canPost,
}: {
  schoolId: string;
  canPost: boolean;
}) {
  const [notes, setNotes] = useState<SchoolNote[] | null>(null);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [composing, setComposing] = useState(false);
  const [shown, setShown] = useState(NOTE_CAP);

  useEffect(() => {
    let cancel = false;
    setNotes(null);
    setError("");
    setComposing(false);
    setShown(NOTE_CAP);
    setBody("");
    api<{ success: boolean; data: SchoolNote[] }>(
      `/api/schools/${encodeURIComponent(schoolId)}/announcements`,
    )
      .then((env) => {
        if (!cancel) setNotes(env.data || []);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [schoolId]);

  const publish = async (event: FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: SchoolNote }>(
        `/api/schools/${encodeURIComponent(schoolId)}/announcements`,
        { body: text },
      );
      setNotes((current) => [env.data, ...(current || [])]);
      setBody("");
      setComposing(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await del(`/api/schools/${encodeURIComponent(schoolId)}/announcements/${encodeURIComponent(id)}`);
      setNotes((current) => (current || []).filter((row) => row.id !== id));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="al-school-manage-panel" id="al-school-announcements">
      <div className="al-school-manage-panel-head">
        <h2 className="al-school-manage-panel-title">School announcement</h2>
      </div>
      <p className="al-school-manage-hint">
        A note for everyone at this school. Students see it on Today. Parents see it with their other announcements.
      </p>
      {error ? <p className="al-school-manage-hint">{error}</p> : null}
      {notes == null && !error ? <p className="al-school-manage-hint">Loading announcements…</p> : null}
      {notes && notes.length === 0 && !canPost ? (
        <p className="al-school-manage-hint">There are no announcements</p>
      ) : null}
      {notes && (notes.length > 0 || canPost) ? (
        <ul className="al-school-announce-list">
          {notes.slice(0, shown).map((note) => (
            <li key={note.id}>
              <div>
                <p>{note.body}</p>
                <span className="al-school-announce-meta">{[note.authorName, postedLabel(note.at)].filter(Boolean).join(" · ")}</span>
              </div>
              {canPost ? (
                <button type="button" className="al-school-announce-remove" aria-label="Remove announcement" disabled={busy} onClick={() => void remove(note.id)}>
                  <X size={14} aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
          {notes.length > NOTE_CAP ? (
            <li className="al-school-announce-more">
              {notes.length > shown ? (
                <button type="button" aria-label="Show more announcements" onClick={() => setShown((count) => count + NOTE_CAP)}>
                  …
                </button>
              ) : (
                <button type="button" onClick={() => setShown(NOTE_CAP)}>
                  Show less
                </button>
              )}
            </li>
          ) : null}
          {canPost && composing ? (
            <li className="al-school-announce-compose">
              <form className="al-school-announce-form" onSubmit={(event) => void publish(event)}>
                <textarea
                  className="al-school-announce-box"
                  value={body}
                  maxLength={2000}
                  placeholder="Write a school announcement"
                  autoFocus
                  onChange={(event) => setBody(event.target.value)}
                />
                <span className="al-school-announce-actions">
                  <button
                    type="button"
                    className="al-schools-btn al-schools-btn--ghost"
                    disabled={busy}
                    onClick={() => {
                      setComposing(false);
                      setBody("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="al-schools-btn" disabled={busy || !body.trim()}>
                    {busy ? "Posting…" : "Post"}
                  </button>
                </span>
              </form>
            </li>
          ) : null}
          {canPost && !composing ? (
            <li className="al-school-announce-add">
              <button type="button" onClick={() => setComposing(true)}>
                <Plus size={15} aria-hidden="true" />
                Add post
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </section>
  );
}
